import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  CAMPAIGN_QUALIFICATION_RULES,
  FILTER_TYPE,
  CAMPAIGN_LIST_STATS_INIT,
} from "../../../common/constants/campaign";
import {
  AudienceList,
  Campaign,
  CampaignListSummary,
  DraftData,
  DraftSendDataType,
  DynamicListType,
  EventKeysReq,
  EventNamesReq,
  FullCampaign,
  OperatorList,
  CAMPAIGN_CONTEXT,
  TemplatePrimaryDetails,
  DynamicListSummary,
  AddFlow,
  ActionOptions,
  TableList,
} from "../../../common/types/campaign";
import { MarketingActivity } from "../../../common/types/person";
import { RootState } from "../../../store";
import {
  INITIAL_PAGINATION_15_ITEMS,
  LOADING_STATES,
} from "../../../common/constants/common";
import {
  createAsyncThunkWrapper,
  initializeLoadingData,
  isSuccess,
} from "../../../common/helper/commonHelper";
import { isEmpty } from "lodash";
import {
  LoadingWithData,
  PaginationType,
  SearchAssetsType,
} from "../../../common/types/common";
import {
  createCampaignApi,
  deleteCampaignApi,
  getCampaignApi,
  publishCampaignApi,
  publishDraftApi,
  saveDraftApi,
  updateCampaignApi,
  listAllCampaignApi,
  addFlowActionApi,
  removeFlowActionApi,
  updateFlowActionApi,
  cloneCampaignApi,
  addFlowEdgeApi,
  removeFlowEdgeApi,
  listCampaignsApi,
  getCampaignListStatsApi,
} from "../../../common/api/campaign/campaign";
import { getTemplatesOfCampaignApi } from "../../../common/api/campaign/reports";
import { filterOperatorsArgCount } from "./helper/validationHelper";
import {
  listAllDynamicListsApi,
  getDynamicListCountApi,
  getDynamicListPreviewApi,
  getEventKeysApi,
  getEventNamesApi,
  getFilterListApi,
  getMarketingActivitiesApi,
  getOperatorsApi,
  getTableDescriptionApi,
  getComputeOperatorsApi,
} from "../../../common/api/campaign/dynamicList";
import {
  ComputeOperatorDetails,
  ComputeOperatorList,
} from "../../../common/types/flow";
import { DESTINATION_TYPES } from "../../../common/types/unifiedMapping";
import { USER_ACCOUNT_STATUS } from "../../../common/types/auth";
import { expireApiStatus } from "../../../common/slices/apiStatusSlice";

export const TEMPLATE_Of_CAMPAIGN_ACTION = "campaign/template-list-filter";
export const DL_LIST_ALL_ACTION = "campaign/dynamic-list/list-all";
const CAMPAIGN_LIST_ALL_ACTION = "campaign/list-all";

interface CampaignState {
  campaignList: PaginationType<Campaign>;
  savingDraft: boolean;
  campaignsChanged: boolean;
  creatingCampaign: LOADING_STATES;
  campaignCreatedId: string;

  dynamicListAll: LoadingWithData<DynamicListSummary[]>;

  filterListLoading: number;
  filterList: {
    [key in CAMPAIGN_CONTEXT]?: {
      [key in FILTER_TYPE]?: LoadingWithData<TableList | {}>;
    };
  };
  tableList: LoadingWithData<TableList | {}>;
  audienceList: LoadingWithData<AudienceList | null>;
  dynamicListCount: LoadingWithData<number | null>;
  campaignDetails: LoadingWithData<FullCampaign>;
  campaignTags: string[];

  publishingCampaign: boolean;
  savingCampaign: boolean;
  editingFlowEdge: boolean;

  dynamicList: {
    marketingEventNamesList: LoadingWithData<MarketingActivity[] | null>;
    productEventNamesList: LoadingWithData<string[] | null>;
    productEventKeysList: LoadingWithData<{ [eventName: string]: string[] }>;
    operators: LoadingWithData<OperatorList | null>;
    computeOperators: LoadingWithData<ComputeOperatorList | null>;
    computeBooleanOperators: ComputeOperatorDetails[];
    operatorsArgCount: { [key: string]: string };
    templateListOfCampaign: {
      [campaignId: string]: LoadingWithData<TemplatePrimaryDetails[]>;
    };
  };
  totalCampaignList: LoadingWithData<CampaignListSummary[]>;
  publishedCampaignList: LoadingWithData<CampaignListSummary[]>;
  activeErrorCheck: boolean;
}

const initDraftData: DraftData = {
  updated_by: null,
  flow: null,
  warning: null,
  schedule: null,
  rule: null,
  updated_at: null,
  dynamic_list: null,
  exit_criteria: null,
};

const initCampaignDetails: FullCampaign = {
  campaign_id: "",
  dynamic_list: null,
  flow: null,
  name: "",
  draft: false,
  schedule: null,
  created_by: { id: 0, name: "" },
  created_at: "",
  version: 0,
  active: true,
  updated_at: "",
  rule: {
    rule: CAMPAIGN_QUALIFICATION_RULES.ONCE,
    recurring_period: null,
    recurring_period_unit: null,
    version: 0,
    created_time: "",
    updated_time: "",
    allow_re_entry: false,
    recurring: false,
    recurring_period_days: null,
  },
  draft_data: initDraftData,
  account_status: USER_ACCOUNT_STATUS.INACTIVE,
  exit_criteria: null,
  is_deleted: false,
};

const initialState: CampaignState = {
  campaignList: INITIAL_PAGINATION_15_ITEMS,
  savingDraft: false,
  campaignsChanged: false,
  creatingCampaign: LOADING_STATES.INIT,
  campaignCreatedId: "",

  dynamicListAll: initializeLoadingData([]),

  filterListLoading: 0,
  filterList: {},

  tableList: initializeLoadingData({}),

  audienceList: initializeLoadingData(null),
  dynamicListCount: initializeLoadingData(null),

  campaignDetails: initializeLoadingData(initCampaignDetails),
  campaignTags: [],

  publishingCampaign: false,
  savingCampaign: false,
  editingFlowEdge: false,

  dynamicList: {
    marketingEventNamesList: initializeLoadingData(null),
    productEventNamesList: initializeLoadingData(null),
    productEventKeysList: initializeLoadingData({}),
    operators: initializeLoadingData(null),
    computeOperators: initializeLoadingData(null),
    computeBooleanOperators: [],
    operatorsArgCount: {},
    templateListOfCampaign: {},
  },
  totalCampaignList: initializeLoadingData([]),
  publishedCampaignList: initializeLoadingData([]),
  activeErrorCheck: false,
};

export const listCampaigns = createAsyncThunk(
  "campaign/list",
  async (
    { searchKeyword, filterColumns, tagsFilter }: SearchAssetsType,
    thunkApi
  ) => {
    const {
      campaign: {
        campaignList: { pageSize, currentPageNo: pageNo },
      },
    } = thunkApi.getState() as RootState;
    return await listCampaignsApi({
      pageSize,
      pageNo,
      searchKeyword,
      filterColumns,
      tagsFilter,
    });
  }
);

export const getCampaignListStats = createAsyncThunk(
  "campaign/list-item-stats",
  async (campaignIds: string[]) => {
    return await getCampaignListStatsApi(campaignIds);
  }
);

export const createCampaign = createAsyncThunk(
  "campaign/create",
  async (
    {
      name,
      campaignContext,
    }: {
      name: string;
      campaignContext?: CAMPAIGN_CONTEXT;
    },
    { dispatch }
  ) => {
    const response = await createCampaignApi(name, campaignContext);
    dispatch(expireApiStatus([{ actionName: CAMPAIGN_LIST_ALL_ACTION }]));
    return response;
  }
);

export const deleteCampaign = createAsyncThunk(
  "campaign/delete",
  async (id: string, { dispatch }) => {
    const response = await deleteCampaignApi(id);
    dispatch(
      expireApiStatus([
        { actionName: CAMPAIGN_LIST_ALL_ACTION },
        { actionName: DL_LIST_ALL_ACTION },
      ])
    );
    return response;
  }
);

export const updateCampaign = createAsyncThunk(
  "campaign/update",
  async ({ id, name }: { id: string; name: string }) => {
    return await updateCampaignApi(id, name);
  }
);

export const getCampaign = createAsyncThunk(
  "campaign/get",
  async (id: string) => {
    return await getCampaignApi(id);
  }
);

export const publishCampaign = createAsyncThunk(
  "campaign/publish-campaign",
  async ({ active }: { active: boolean }, { dispatch, getState }) => {
    const { campaign } = getState() as RootState;
    const response = await publishCampaignApi(
      campaign.campaignDetails.data.campaign_id,
      active
    );
    dispatch(
      expireApiStatus([
        { actionName: CAMPAIGN_LIST_ALL_ACTION },
        { actionName: DL_LIST_ALL_ACTION },
      ])
    );
    return response;
  }
);

export const addFlowAction = createAsyncThunk(
  "campaign/add-flow-actions",
  async (flow: AddFlow) => {
    return await addFlowActionApi(flow);
  }
);

export const removeFlowAction = createAsyncThunk(
  "campaign/remove-flow-actions",
  async (
    { actionId, groupId }: { actionId: string; groupId?: string },
    thunkApi
  ) => {
    const { campaign } = thunkApi.getState() as RootState;
    return await removeFlowActionApi(
      campaign.campaignDetails.data.campaign_id,
      actionId,
      groupId
    );
  }
);

export const updateFlowAction = createAsyncThunk(
  "campaign/update-flow-actions",
  async (
    {
      actionId,
      actionOptions,
      groupId,
      shouldRender = false, // used for reducer
    }: {
      actionId: string;
      actionOptions: ActionOptions;
      groupId?: string;
      shouldRender?: boolean;
    },
    thunkApi
  ) => {
    const { campaign } = thunkApi.getState() as RootState;
    return await updateFlowActionApi({
      campaign_id: campaign.campaignDetails.data.campaign_id,
      action_id: actionId,
      action_options: actionOptions,
      parent_action_id: groupId,
    });
  }
);

export const addFlowEdge = createAsyncThunk(
  "campaign/flow/add-edge",
  async (
    {
      source,
      target,
      groupId,
      conditionId,
      branchId,
    }: {
      source: string;
      target: string;
      groupId?: string;
      conditionId?: string;
      branchId?: string;
    },
    thunkApi
  ) => {
    const { campaign } = thunkApi.getState() as RootState;
    return await addFlowEdgeApi({
      campaign_id: campaign.campaignDetails.data.campaign_id,
      source_action_id: source,
      target_action_id: target,
      parent_action_id: groupId,
      link_attributes: conditionId
        ? {
            edge_type: "group_edge",
            condition_id: conditionId,
            branch_id: branchId,
          }
        : undefined,
    });
  }
);

export const removeFlowEdge = createAsyncThunk(
  "campaign/flow/remove-edge",
  async (
    {
      source,
      groupId,
      conditionId,
    }: { source: string; groupId?: string; conditionId?: string },
    thunkApi
  ) => {
    const { campaign } = thunkApi.getState() as RootState;
    return await removeFlowEdgeApi({
      campaign_id: campaign.campaignDetails.data.campaign_id,
      action_id: source,
      parent_action_id: groupId,
      condition_id: conditionId,
    });
  }
);

export const getFilterList = createAsyncThunkWrapper({
  actionName: "campaign/get-filter-list",
  dispatchFn: async ({
    filterType,
    campaignContext,
  }: {
    filterType: FILTER_TYPE;
    campaignContext: CAMPAIGN_CONTEXT;
  }) => {
    return await getFilterListApi(filterType, campaignContext);
  },
  isCachable: true,
});

export const getTableDescription = createAsyncThunkWrapper({
  actionName: "campaign/get-table-description",
  dispatchFn: async () => {
    return await getTableDescriptionApi();
  },
  isCachable: true,
});

export const getDynamicListPreview = createAsyncThunk(
  "campaign/get-dynamic-list-preview",
  async (
    {
      query,
      searchKeyword,
      filterColumns,
    }: {
      query: DynamicListType[];
      searchKeyword: string;
      filterColumns: string[];
    },
    thunkApi
  ) => {
    const {
      campaign: { campaignDetails },
    } = thunkApi.getState() as RootState;
    return await getDynamicListPreviewApi(
      query,
      searchKeyword,
      filterColumns,
      campaignDetails.data.campaign_context
    );
  }
);

export const saveCampaignDraft = createAsyncThunk(
  "campaign/dynamic-list/save-draft",
  async (data: DraftSendDataType, thunkApi) => {
    const { campaign } = thunkApi.getState() as RootState;
    thunkApi.dispatch(setSavingDraft(true));
    return saveDraftApi({
      campaign_id: campaign.campaignDetails.data.campaign_id,
      draft: data,
    });
  },
  {
    condition: (_, { getState }) => {
      const { campaign } = getState() as RootState;
      if (!campaign.campaignDetails.data.campaign_id) {
        return false;
      }
    },
  }
);

export const publishCampaignDraft = createAsyncThunk(
  "campaign/dynamic-list/publish-draft",
  async (_, thunkApi) => {
    const { campaign } = thunkApi.getState() as RootState;
    return publishDraftApi(campaign.campaignDetails.data.campaign_id);
  }
);

export const getDynamicListCount = createAsyncThunk(
  "campaign/dynamic-list-count",
  async (campaignId: string) => {
    return getDynamicListCountApi(campaignId);
  }
);

export const getEventNames = createAsyncThunk(
  "campaign/get-event-names",
  async (data: EventNamesReq) => {
    return await getEventNamesApi(data);
  }
);

export const getEventKeys = createAsyncThunk(
  "campaign/get-event-keys",
  async (data: EventKeysReq) => {
    return await getEventKeysApi(data);
  }
);

export const getOperators = createAsyncThunkWrapper({
  actionName: "campaign/get-operators",
  dispatchFn: async () => {
    return await getOperatorsApi();
  },
  isCachable: true,
});

export const getComputeOperators = createAsyncThunkWrapper({
  actionName: "campaign/get-compute-operators",
  dispatchFn: async () => {
    return await getComputeOperatorsApi();
  },
  isCachable: true,
});

export const getMarketingActivities = createAsyncThunkWrapper({
  actionName: "campaign/get-marketing-activities",
  dispatchFn: async () => {
    return await getMarketingActivitiesApi();
  },
  isCachable: true,
});

export const getTemplatesOfCampaign = createAsyncThunkWrapper({
  actionName: TEMPLATE_Of_CAMPAIGN_ACTION,
  dispatchFn: async (id: string) => {
    return await getTemplatesOfCampaignApi(id);
  },
  isCachable: true,
});

export const listAllCampaigns = createAsyncThunkWrapper({
  actionName: CAMPAIGN_LIST_ALL_ACTION,
  dispatchFn: async () => {
    return await listAllCampaignApi();
  },
  isCachable: true,
});

export const listAllDynamicLists = createAsyncThunkWrapper({
  actionName: DL_LIST_ALL_ACTION,
  dispatchFn: async () => {
    return await listAllDynamicListsApi();
  },
  isCachable: true,
});

export const cloneCampaign = createAsyncThunk(
  "campaign/clone",
  async (
    { campaignId, name }: { campaignId: string; name: string },
    { dispatch }
  ) => {
    const response = await cloneCampaignApi({
      campaignId,
      clonedCampaignName: name,
    });
    dispatch(
      expireApiStatus([
        { actionName: CAMPAIGN_LIST_ALL_ACTION },
        { actionName: DL_LIST_ALL_ACTION },
      ])
    );
    return response;
  }
);

const campaignSlice = createSlice({
  name: "campaign",
  initialState,
  reducers: {
    setCampaignsPage(state, action: PayloadAction<number>) {
      const pageNo = action.payload;
      if (pageNo && pageNo <= (state.campaignList.totalPageCount ?? 1)) {
        state.campaignList.currentPageNo = pageNo;
        state.campaignList.changingPage = true;
      }
    },
    setCampaignDetails(state, action: PayloadAction<FullCampaign>) {
      state.campaignDetails.data = action.payload;
    },
    clearCampaignCreatedId(state) {
      state.campaignCreatedId = "";
    },
    clearCampaignDetails(state) {
      state.campaignDetails = initializeLoadingData(initCampaignDetails);
      state.audienceList = initializeLoadingData(null);
      state.filterListLoading = 0;
      state.dynamicListCount = initializeLoadingData(null);
      if (!isSuccess(state.dynamicList.productEventNamesList.loading)) {
        state.dynamicList.productEventNamesList.loading = LOADING_STATES.INIT;
      }
      if (!isSuccess(state.tableList.loading)) {
        state.tableList.loading = LOADING_STATES.INIT;
      }
      state.activeErrorCheck = false;
    },
    clearTableListStatus(state) {
      if (!isSuccess(state.tableList.loading)) {
        state.tableList.loading = LOADING_STATES.INIT;
      }
    },
    clearCampaignList(state) {
      state.campaignList = initialState.campaignList;
    },
    setActiveErrorCheck(state, action) {
      state.activeErrorCheck = action.payload;
    },
    setPublishLoadState(state, action) {
      state.publishingCampaign = action.payload;
    },
    setSavingDraft(state, action: PayloadAction<boolean>) {
      state.savingDraft = action.payload;
    },
    setCampaignTags(state, action: PayloadAction<string[]>) {
      state.campaignTags = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // campaign list
      .addCase(listCampaigns.pending, (state) => {
        state.campaignList.fetchingList = true;
      })
      .addCase(listCampaigns.fulfilled, (state, action) => {
        state.campaignList.list = action.payload.records.map((campaign) => {
          return {
            ...campaign,
            ...CAMPAIGN_LIST_STATS_INIT,
          };
        });
        state.campaignList.totalPageCount = action.payload.page_count;
        state.campaignList.fetchingList = false;
        state.campaignList.changingPage = false;
      })
      .addCase(listCampaigns.rejected, (state) => {
        state.campaignList.totalPageCount = 0;
        state.campaignList.fetchingList = false;
        state.campaignList.changingPage = false;
      })

      // campaign list stats
      .addCase(getCampaignListStats.pending, (state) => {
        state.campaignList.list =
          state.campaignList.list?.map((campaign) => {
            campaign.stats_loading = LOADING_STATES.LOADING;
            return campaign;
          }) ?? null;
      })
      .addCase(getCampaignListStats.fulfilled, (state, action) => {
        state.campaignList.list =
          state.campaignList.list?.map((campaign) => {
            const stats =
              action.payload.records.find(
                (cStats) => cStats.campaign_id === campaign.campaign_id
              ) ?? {};
            return {
              ...campaign,
              ...stats,
              stats_loading: LOADING_STATES.SUCCESS,
            };
          }) ?? null;
      })
      .addCase(getCampaignListStats.rejected, (state) => {
        state.campaignList.list =
          state.campaignList.list?.map((campaign) => {
            campaign.stats_loading = LOADING_STATES.FAILED;
            return campaign;
          }) ?? null;
      })

      // Create campaign
      .addCase(createCampaign.pending, (state) => {
        state.creatingCampaign = LOADING_STATES.LOADING;
      })
      .addCase(createCampaign.fulfilled, (state, action) => {
        state.creatingCampaign = LOADING_STATES.SUCCESS;
        state.campaignCreatedId = action.payload.campaign.campaign_id;
        toast.success("New journey added");
      })
      .addCase(createCampaign.rejected, (state) => {
        state.creatingCampaign = LOADING_STATES.FAILED;
      })
      // Delete Campaign
      .addCase(deleteCampaign.fulfilled, (state) => {
        state.campaignsChanged = !state.campaignsChanged;
        toast.success("Journey removed");
      })
      .addCase(deleteCampaign.rejected, () => {})
      // Update Campaign
      .addCase(updateCampaign.fulfilled, (state) => {
        state.campaignsChanged = !state.campaignsChanged;
        toast.success("Journey updated");
      })
      .addCase(updateCampaign.rejected, () => {})

      // Get campaign details
      .addCase(getCampaign.pending, (state) => {
        state.campaignDetails.loading = LOADING_STATES.LOADING;
      })
      .addCase(getCampaign.fulfilled, (state, action) => {
        state.campaignDetails.loading = LOADING_STATES.SUCCESS;
        state.campaignDetails.data = {
          ...{ draft_data: initDraftData },
          ...action.payload.campaign,
        };
        state.campaignTags = action.payload.campaign.tags ?? [];
      })
      .addCase(getCampaign.rejected, (state) => {
        state.campaignDetails.data = initCampaignDetails;
        state.campaignDetails.loading = LOADING_STATES.FAILED;
      })

      // Create and update flow
      .addCase(addFlowAction.pending, (state) => {
        state.savingCampaign = true;
      })
      .addCase(addFlowAction.fulfilled, (state, action) => {
        state.savingCampaign = false;
        state.campaignDetails.data.draft_data.flow = action.payload.flow;
        state.campaignDetails.data.draft = true;
        state.campaignDetails.data.draft_data.updated_at =
          action.payload.updated_at;
        state.campaignDetails.data.draft_data.updated_by =
          action.payload.updated_by;
      })
      .addCase(addFlowAction.rejected, (state) => {
        state.savingCampaign = false;
      })
      // delete flow
      .addCase(removeFlowAction.pending, (state) => {
        state.savingCampaign = true;
      })
      .addCase(removeFlowAction.fulfilled, (state, action) => {
        state.savingCampaign = false;
        state.campaignDetails.data.draft_data.flow = action.payload.flow;
        state.campaignDetails.data.draft = true;
        state.campaignDetails.data.draft_data.updated_at =
          action.payload.updated_at;
        state.campaignDetails.data.draft_data.updated_by =
          action.payload.updated_by;
      })
      .addCase(removeFlowAction.rejected, (state) => {
        state.savingCampaign = false;
      })
      // update flow
      .addCase(updateFlowAction.pending, (state) => {
        state.savingCampaign = true;
      })
      .addCase(updateFlowAction.fulfilled, (state, action) => {
        state.savingCampaign = false;
        state.campaignDetails.data.draft = true;
        state.campaignDetails.data.draft_data.updated_at =
          action.payload.updated_at;
        state.campaignDetails.data.draft_data.updated_by =
          action.payload.updated_by;
        if (action.meta.arg.shouldRender) {
          state.campaignDetails.data.draft_data.flow = action.payload.flow;
        }
      })
      .addCase(updateFlowAction.rejected, (state) => {
        state.savingCampaign = false;
      })

      //Add flow edge
      .addCase(addFlowEdge.pending, (state) => {
        state.savingCampaign = true;
        state.editingFlowEdge = true;
      })
      .addCase(addFlowEdge.fulfilled, (state, action) => {
        state.savingCampaign = false;
        state.campaignDetails.data.draft_data.flow = action.payload.flow;
        state.campaignDetails.data.draft = true;
        state.campaignDetails.data.draft_data.updated_at =
          action.payload.updated_at;
        state.campaignDetails.data.draft_data.updated_by =
          action.payload.updated_by;
        state.editingFlowEdge = false;
      })
      .addCase(addFlowEdge.rejected, (state) => {
        state.savingCampaign = false;
        state.editingFlowEdge = false;
      })

      //Remove flow edge
      .addCase(removeFlowEdge.pending, (state) => {
        state.savingCampaign = true;
        state.editingFlowEdge = true;
      })
      .addCase(removeFlowEdge.fulfilled, (state, action) => {
        state.savingCampaign = false;
        state.campaignDetails.data.draft_data.flow = action.payload.flow;
        state.campaignDetails.data.draft = true;
        state.campaignDetails.data.draft_data.updated_at =
          action.payload.updated_at;
        state.campaignDetails.data.draft_data.updated_by =
          action.payload.updated_by;
        state.editingFlowEdge = false;
      })
      .addCase(removeFlowEdge.rejected, (state) => {
        state.savingCampaign = false;
        state.editingFlowEdge = false;
      })

      // Filter list
      .addCase(getFilterList.pending, (state, action) => {
        state.filterListLoading += 1;
        const context =
          action.meta.arg.campaignContext ?? CAMPAIGN_CONTEXT.PERSON;
        state.filterList[context] = {
          ...state.filterList[context],
          [action.meta.arg.filterType]: {
            loading: LOADING_STATES.LOADING,
            data:
              state.filterList[context]?.[action.meta.arg.filterType]?.data ??
              {},
          },
        };
      })
      .addCase(getFilterList.fulfilled, (state, action) => {
        state.filterListLoading -= 1;
        const context =
          action.meta.arg.campaignContext ?? CAMPAIGN_CONTEXT.PERSON;
        const oldData =
          state.filterList[context]?.[action.meta.arg.filterType]?.data ?? {};
        state.filterList[context] = {
          ...state.filterList[context],
          [action.meta.arg.filterType]: {
            loading: LOADING_STATES.SUCCESS,
            data: { ...oldData, ...action.payload },
          },
        };
      })
      .addCase(getFilterList.rejected, (state, action) => {
        state.filterListLoading -= 1;
        const context =
          action.meta.arg.campaignContext ?? CAMPAIGN_CONTEXT.PERSON;
        state.filterList[context] = {
          ...state.filterList[context],
          [action.meta.arg.filterType]: {
            loading: LOADING_STATES.FAILED,
            data:
              state.filterList[context]?.[action.meta.arg.filterType]?.data ??
              {},
          },
        };
      })

      // Table list
      .addCase(getTableDescription.pending, (state) => {
        state.tableList.loading = LOADING_STATES.LOADING;
      })
      .addCase(getTableDescription.fulfilled, (state, action) => {
        state.tableList.data = action.payload;
        state.tableList.loading = LOADING_STATES.SUCCESS;
      })
      .addCase(getTableDescription.rejected, (state) => {
        state.tableList.loading = LOADING_STATES.FAILED;
      })

      // Dynamic list preview
      .addCase(getDynamicListPreview.pending, (state) => {
        state.audienceList.data = {
          rows: [],
          total_count: {
            count: 0,
          },
        };
        state.audienceList.loading = LOADING_STATES.LOADING;
      })
      .addCase(getDynamicListPreview.fulfilled, (state, action) => {
        state.audienceList.data = action.payload.audience_list;
        state.audienceList.loading = LOADING_STATES.SUCCESS;
      })
      .addCase(getDynamicListPreview.rejected, (state) => {
        state.audienceList.loading = LOADING_STATES.FAILED;
      })

      // Publish Campaign
      .addCase(publishCampaign.pending, (state) => {
        state.publishingCampaign = true;
      })
      .addCase(publishCampaign.fulfilled, (state, action) => {
        const data = action.payload;
        state.campaignDetails.data.active = data.campaign.active;
        state.campaignDetails.data.updated_at = new Date().toString();
        state.campaignDetails.data.draft = false;
        state.campaignDetails.data.draft_data = initDraftData;
        state.publishingCampaign = false;
        toast.success(
          `Campaign published as ${
            data.campaign.active ? "active" : "inactive"
          }`
        );
      })
      .addCase(publishCampaign.rejected, (state) => {
        state.publishingCampaign = false;
      })

      // Drafts
      .addCase(saveCampaignDraft.fulfilled, (state, action) => {
        const { updated_by, updated_at, warning } = action.payload.draft;
        state.campaignDetails.data.draft_data.updated_by = updated_by;
        state.campaignDetails.data.draft_data.updated_at = updated_at;
        state.campaignDetails.data.draft_data.warning = warning;
        state.campaignDetails.data.draft = true;
        state.savingDraft = false;
      })
      .addCase(saveCampaignDraft.pending, (state, action) => {
        state.savingDraft = true;
      })
      .addCase(saveCampaignDraft.rejected, (state, action) => {
        state.savingDraft = false;
      })

      // Reports
      .addCase(getDynamicListCount.pending, (state, action) => {
        state.dynamicListCount.loading = LOADING_STATES.LOADING;
      })
      .addCase(getDynamicListCount.fulfilled, (state, action) => {
        state.dynamicListCount.data = action.payload.count;
        state.dynamicListCount.loading = LOADING_STATES.SUCCESS;
      })
      .addCase(getDynamicListCount.rejected, (state) => {
        state.dynamicListCount.data = 0;
        state.dynamicListCount.loading = LOADING_STATES.FAILED;
      })
      .addCase(getEventNames.pending, (state) => {
        state.dynamicList.productEventNamesList.loading =
          LOADING_STATES.LOADING;
      })
      .addCase(getEventNames.fulfilled, (state, action) => {
        state.dynamicList.productEventNamesList.data = action.payload.rows;
        state.dynamicList.productEventNamesList.loading =
          LOADING_STATES.SUCCESS;
      })
      .addCase(getEventNames.rejected, (state) => {
        state.dynamicList.productEventNamesList.loading = LOADING_STATES.FAILED;
      })

      .addCase(getEventKeys.pending, (state) => {
        state.dynamicList.productEventKeysList.loading = LOADING_STATES.LOADING;
      })
      .addCase(getEventKeys.fulfilled, (state, action) => {
        if (!isEmpty(action.payload)) {
          state.dynamicList.productEventKeysList.data = {
            ...state.dynamicList.productEventKeysList.data,
            ...action.payload,
          };
        }
        state.dynamicList.productEventKeysList.loading = LOADING_STATES.SUCCESS;
      })
      .addCase(getEventKeys.rejected, (state) => {
        state.dynamicList.productEventKeysList.loading = LOADING_STATES.FAILED;
      })

      .addCase(getOperators.pending, (state) => {
        state.dynamicList.operators.loading = LOADING_STATES.LOADING;
      })
      .addCase(getOperators.fulfilled, (state, action) => {
        state.dynamicList.operators.data = action.payload;
        state.dynamicList.operatorsArgCount = filterOperatorsArgCount(
          action.payload
        );

        state.dynamicList.operators.loading = LOADING_STATES.SUCCESS;
      })
      .addCase(getOperators.rejected, (state) => {
        state.dynamicList.operators.loading = LOADING_STATES.FAILED;
      })
      .addCase(getComputeOperators.pending, (state) => {
        state.dynamicList.computeOperators.loading = LOADING_STATES.LOADING;
      })
      .addCase(getComputeOperators.fulfilled, (state, action) => {
        state.dynamicList.computeOperators.data = action.payload;
        state.dynamicList.computeBooleanOperators = Object.values(
          action.payload[DESTINATION_TYPES.BOOLEAN]
        );
        state.dynamicList.computeOperators.loading = LOADING_STATES.SUCCESS;
      })
      .addCase(getComputeOperators.rejected, (state) => {
        state.dynamicList.computeOperators.loading = LOADING_STATES.FAILED;
      })
      .addCase(getMarketingActivities.pending, (state) => {
        state.dynamicList.marketingEventNamesList.loading =
          LOADING_STATES.LOADING;
      })
      .addCase(getMarketingActivities.fulfilled, (state, action) => {
        if (!isEmpty(action.payload)) {
          state.dynamicList.marketingEventNamesList.data =
            action.payload.marketing_activity;
        }
        state.dynamicList.marketingEventNamesList.loading =
          LOADING_STATES.SUCCESS;
      })
      .addCase(getMarketingActivities.rejected, (state) => {
        state.dynamicList.marketingEventNamesList.loading =
          LOADING_STATES.FAILED;
      })

      .addCase(getTemplatesOfCampaign.pending, (state, action) => {
        state.dynamicList.templateListOfCampaign[action.meta.arg] = {
          loading: LOADING_STATES.LOADING,
          data: [],
        };
      })
      .addCase(getTemplatesOfCampaign.fulfilled, (state, action) => {
        state.dynamicList.templateListOfCampaign[action.meta.arg] = {
          data: action.payload.templates,
          loading: LOADING_STATES.SUCCESS,
        };
      })
      .addCase(getTemplatesOfCampaign.rejected, (state, action) => {
        if (
          !state.dynamicList.templateListOfCampaign[action.meta.arg]?.data
            ?.length
        ) {
          state.dynamicList.templateListOfCampaign[action.meta.arg] = {
            data: [],
            loading: LOADING_STATES.FAILED,
          };
        }
      })

      .addCase(listAllCampaigns.pending, (state) => {
        state.totalCampaignList.loading = LOADING_STATES.LOADING;
        state.publishedCampaignList.loading = LOADING_STATES.LOADING;
      })
      .addCase(listAllCampaigns.fulfilled, (state, action) => {
        state.totalCampaignList = {
          data: action.payload.records,
          loading: LOADING_STATES.SUCCESS,
        };
        state.publishedCampaignList = {
          data: action.payload.records.filter(
            (item) => item.activated_at || item.deactivated_at
          ),
          loading: LOADING_STATES.SUCCESS,
        };
      })
      .addCase(listAllCampaigns.rejected, (state) => {
        state.totalCampaignList.loading = LOADING_STATES.FAILED;
        state.publishedCampaignList.loading = LOADING_STATES.FAILED;
      })

      .addCase(listAllDynamicLists.pending, (state) => {
        state.dynamicListAll.loading = LOADING_STATES.LOADING;
      })
      .addCase(listAllDynamicLists.fulfilled, (state, action) => {
        state.dynamicListAll.data = action.payload.records;
        state.dynamicListAll.loading = LOADING_STATES.SUCCESS;
      })
      .addCase(listAllDynamicLists.rejected, (state) => {
        state.dynamicListAll.loading = LOADING_STATES.FAILED;
      })

      //clone campaign
      .addCase(cloneCampaign.pending, (state) => {
        state.creatingCampaign = LOADING_STATES.LOADING;
      })
      .addCase(cloneCampaign.fulfilled, (state, { payload }) => {
        state.creatingCampaign = LOADING_STATES.SUCCESS;
        if (payload.campaign.campaign_id) {
          state.campaignCreatedId = payload.campaign.campaign_id;
          toast.success("Journey cloned successfully");
        }
      })
      .addCase(cloneCampaign.rejected, (state) => {
        state.creatingCampaign = LOADING_STATES.FAILED;
        toast.error("Journey cloning failed");
      });
  },
});

export const {
  setCampaignsPage,
  clearCampaignCreatedId,
  clearCampaignDetails,
  clearTableListStatus,
  clearCampaignList,
  setActiveErrorCheck,
  setCampaignDetails,
  setPublishLoadState,
  setSavingDraft,
  setCampaignTags,
} = campaignSlice.actions;

export const selectCampaign = (state: RootState) => state.campaign;

export default campaignSlice.reducer;
